import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { Container } from '../../../ui/gridSystem';
import { CTA, Text } from '../../../ui/atoms';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import { MY_USERS_GET } from '../../redux/actions/statistics';
import { ChartBlock, FullPage, PrintBtnBanner, PrintContainer } from './style';
import storage from '../../api/storage';
import UserChart from './UserChart';
import BarChart from '../../../ui/components/BarChart';
import {
  barChartConfig,
  barChartConfigWithTrend,
} from '../../../utils/course-state';
import {
  ACCESS_STATS_CHART_OPTS,
  arrayToKeyValue,
  formatAccessStats,
} from '../../../utils/charts';
import {
  ACCESS_STATISTICS_LIST_GET,
  ACTIVITY_STATISTICS_LIST_GET,
  CATEGORY_STATISTICS_LIST_GET,
  COURSE_STATISTICS_LIST_GET,
  TUTOR_STATISTICS_LIST_GET,
} from '../../redux/actions/supervisorStats';
import CircularStats from '../../../ui/atoms/CircularStats';
import { getFilterLabel } from '../../../utils/labels';

const DownloadPdf = ({
  mediaIsPhone,
  isSupervisor,
  myUsers,
  getMyUsers,
  accesses,
  getAccessStatisticsList,
  tutors,
  getTutorStatisticsList,
  categories,
  getCategoryStatisticsList,
  courses,
  getCourseStatisticsList,
  activities,
  getActivityStatisticsList,
  isSpinning
}) => {
  const [filters, setFilters] = useState({});
  useEffect(() => {
    const storageFilters = storage.read('filters')?.value;
    setFilters(storageFilters);
  }, []);

  useEffect(() => {
    // users
    if (filters?.users) {
      filters?.users?.filter?.forEach((filter) => {
        getMyUsers({ ...filters?.users, filter: filter?.value });
      });
    }
    // access
    if (filters?.access) {
      getAccessStatisticsList(filters?.access);
    }
    // tutors
    if (isSupervisor) {
      getTutorStatisticsList();
    }
    // categories
    if (filters?.categories) {
      getCategoryStatisticsList(filters?.categories);
    }
    // courses
    if (filters?.courses) {
      filters?.courses?.filter?.forEach((filter) => {
        getCourseStatisticsList({ ...filters?.courses, filter: filter?.value });
      });
    }
    // activities
    if (filters?.activities) {
      getActivityStatisticsList(filters?.activities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isSupervisor]);

  const getPages = (userFilters) => {
    let pages = 0;
    if (userFilters.includes('ffoo')) pages += 1;
    if (userFilters.includes('region')) pages += 0.52;
    if (userFilters.includes('province')) pages += 0.52;
    return pages;
  };

  return (
    <PrintContainer>
      <PrintBtnBanner>
        <Container>
          <CTA
            type="primary"
            btnText="Stampa"
            onClick={() => window.print()}
            icon="printer"
            disabled={isSpinning}
          />
        </Container>
      </PrintBtnBanner>
      <Container>
        {filters?.users?.filter?.length > 0 && (
          <FullPage
            pages={getPages(filters?.users?.filter?.map((f) => f.value))}
          >
            <Text type="h1.5" mb={32}>
              Utenti
            </Text>
            {myUsers &&
              filters?.users?.filter?.map(({ value: key }, i) => (
                <ChartBlock blocks={1.6} key={`charts-${key}-${i}`}>
                  <UserChart
                    filterKey={key}
                    data={myUsers[key]}
                    isSupervisor={isSupervisor}
                  />
                </ChartBlock>
              ))}
          </FullPage>
        )}
        {isSupervisor && Object.keys(filters?.access || {})?.length > 0 && (
          <ChartBlock>
            <Text type="h1.5" mb={16}>
              Accessi
            </Text>
            <Text type="b3" mb={16} className="capitalize">
              {dayjs(filters?.access?.start_date, 'DD-MM-YYYY')?.format(
                'MMMM YYYY'
              )}
            </Text>
            <div className="chart-wrapper">
              <BarChart
                config={barChartConfigWithTrend(
                  formatAccessStats(accesses?.list, filters?.access?.start_date)
                )}
                customOptions={ACCESS_STATS_CHART_OPTS}
              />
            </div>
          </ChartBlock>
        )}
        {isSupervisor && (
          <ChartBlock>
            <Text type="h1.5" mb={32} mt={16}>
              Tutor accreditati
            </Text>
            <CircularStats
              label="Tutor totali"
              size={mediaIsPhone ? 190 : 145}
              data={arrayToKeyValue(tutors?.list, 'label', 'count')}
            />
          </ChartBlock>
        )}
        {isSupervisor && Object.keys(filters?.categories || {})?.length > 0 && (
          <ChartBlock>
            <Text type="h1.5" mb={32} mt={16}>
              Aree tematiche
            </Text>
            <div className="chart-wrapper">
              <BarChart
                config={barChartConfig(
                  arrayToKeyValue(categories?.list, 'name', 'category_count')
                )}
                horizontal
              />
            </div>
          </ChartBlock>
        )}
        {isSupervisor && filters?.courses?.filter?.length > 0 && (
          <ChartBlock blocks={Object.keys(courses)?.length}>
            <Text type="h1.5" mb={32}>
              Percorsi formativi
            </Text>
            {Object.keys(courses || {}).map((c) => (
              <>
                <Text type="h2.5" mb={32}>
                  {getFilterLabel(c)}
                </Text>
                <div className="chart-wrapper">
                  <BarChart
                    config={barChartConfig(
                      arrayToKeyValue(courses[c], 'label', 'count')
                    )}
                  />
                </div>
              </>
            ))}
          </ChartBlock>
        )}
        {isSupervisor && Object.keys(filters?.activities || {})?.length > 0 && (
          <ChartBlock>
            <Text type="h1.5" mb={32}>
              Attività
            </Text>
            <div className="chart-wrapper">
              <BarChart
                config={barChartConfig(
                  arrayToKeyValue(
                    activities?.list,
                    'category_name',
                    'category_count'
                  )
                )}
              />
            </div>
          </ChartBlock>
        )}
      </Container>
    </PrintContainer>
  );
};

DownloadPdf.propTypes = {
  mediaIsPhone: PropTypes.bool,
  myUsers: PropTypes.object,
  isSupervisor: PropTypes.bool,
  getMyUsers: PropTypes.func,
  accesses: PropTypes.object,
  getAccessStatisticsList: PropTypes.func,
  tutors: PropTypes.object,
  getTutorStatisticsList: PropTypes.func,
  categories: PropTypes.object,
  getCategoryStatisticsList: PropTypes.func,
  courses: PropTypes.object,
  getCourseStatisticsList: PropTypes.func,
  activities: PropTypes.object,
  getActivityStatisticsList: PropTypes.func,
  isSpinning: PropTypes.bool,
};

export default connect(
  (state) => {
    const { isSupervisor } = state.user;
    const { users: myUsers, courses } = state.statistics.printCharts;
    const { accesses, tutors, categories, activities } =
      state.supervisorStats.charts;
    const { isSpinning } = state.app.loader;

    return {
      isSupervisor,
      myUsers,
      accesses,
      tutors,
      categories,
      courses,
      activities,
      isSpinning,
    };
  },
  (dispatch) => ({
    getMyUsers: (filters) =>
      dispatch({
        type: MY_USERS_GET._REQUEST,
        filters,
        status_array: ['assignments', 'fruitions', 'completions'],
      }),
    getAccessStatisticsList: (filters) =>
      dispatch({ type: ACCESS_STATISTICS_LIST_GET._REQUEST, filters }),
    getTutorStatisticsList: () =>
      dispatch({ type: TUTOR_STATISTICS_LIST_GET._REQUEST }),
    getCategoryStatisticsList: (filters) =>
      dispatch({ type: CATEGORY_STATISTICS_LIST_GET._REQUEST, filters }),
    getCourseStatisticsList: (filters) =>
      dispatch({ type: COURSE_STATISTICS_LIST_GET._REQUEST, filters }),
    getActivityStatisticsList: (filters) =>
      dispatch({ type: ACTIVITY_STATISTICS_LIST_GET._REQUEST, filters }),
  })
)(withMediaQueries(DownloadPdf));
